import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
// Static data for dropdowns and questions
const staticCourseData = [
  { id: 1, name: 'Computer Science' },
  { id: 2, name: 'Mathematics' },
  { id: 3, name: 'Physics' },
  { id: 4, name: 'Chemistry' },
  { id: 5, name: 'Biology' },
  { id: 6, name: 'Geography' },
  { id: 7, name: 'Literature' },
];
const staticModuleData = [
  { id: 1, name: 'Introduction' },
  { id: 2, name: 'Advanced' },
  { id: 3, name: 'Practical' },
  { id: 4, name: 'World Capitals' },
  { id: 5, name: 'Plant Physiology' },
  { id: 6, name: 'Basic Chemistry' },
  { id: 7, name: 'Shakespeare' },
  { id: 8, name: 'Algebra' },
];
const staticTopicData = [
  { id: 1, name: 'Basics' },
  { id: 2, name: 'Intermediate' },
  { id: 3, name: 'Complex' },
  { id: 4, name: 'Europe' },
  { id: 5, name: 'Photosynthesis' },
  { id: 6, name: 'Chemical Formulas' },
  { id: 7, name: 'Plays' },
  { id: 8, name: 'Linear Equations' },
];
const staticQuestionTypeData = [
  { id: 1, name: 'Multiple Choice' },
  { id: 2, name: 'Descriptive' },
  { id: 3, name: 'Short Answer' },
  { id: 4, name: 'Problem Solving' },
];
const staticQuestionData = [
  {
    questionId: 1,
    questionText: 'What is the capital of France?',
    course: 'Geography',
    module: 'World Capitals',
    topic: 'Europe',
    type: 'Multiple Choice',
  },
  {
    questionId: 2,
    questionText: 'Explain the process of photosynthesis.',
    course: 'Biology',
    module: 'Plant Physiology',
    topic: 'Photosynthesis',
    type: 'Descriptive',
  },
  {
    questionId: 3,
    questionText: 'What is the formula for water?',
    course: 'Chemistry',
    module: 'Basic Chemistry',
    topic: 'Chemical Formulas',
    type: 'Short Answer',
  },
  {
    questionId: 4,
    questionText: 'Who wrote "Romeo and Juliet"?',
    course: 'Literature',
    module: 'Shakespeare',
    topic: 'Plays',
    type: 'Multiple Choice',
  },
  {
    questionId: 5,
    questionText: 'Solve for x: 2x + 3 = 7',
    course: 'Mathematics',
    module: 'Algebra',
    topic: 'Linear Equations',
    type: 'Problem Solving',
  },
];

const EditQuestion = () => {
  const theme = useTheme();
  const { questionId } = useParams();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
const [snackbarMessage, setSnackbarMessage] = useState('');
const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    course: '',
    module: '',
    topic: '',
    questionType: '',
    questionText: '',
  });

  useEffect(() => {
    const question = staticQuestionData.find(q => q.questionId === parseInt(questionId));
    if (question) {
      setFormData({
        course: question.course,
        module: question.module,
        topic: question.topic,
        questionType: question.type,
        questionText: question.questionText,
      });
    }
  }, [questionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!formData.course || !formData.module || !formData.topic || !formData.questionType || !formData.questionText) {
      setSnackbarMessage('plz fill rquired field!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("error");
      return;
    }
    setShowSuccessModal(true)
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
           <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    Question Details
                  </Typography>
                  
                </Box>
        <form onSubmit={handleSave}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth required sx={{ '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main } }}>
              <InputLabel>Course Name</InputLabel>
              <Select
                name="course"
                value={formData.course}
                label="Course Name"
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {staticCourseData.map((course) => (
                  <MenuItem key={course.id} value={course.name}>
                    {course.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={{ '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main } }}>
              <InputLabel>Module Name</InputLabel>
              <Select
                name="module"
                value={formData.module}
                label="Module Name"
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {staticModuleData.map((module) => (
                  <MenuItem key={module.id} value={module.name}>
                    {module.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl fullWidth required sx={{ '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main } }}>
              <InputLabel>Topic Name</InputLabel>
              <Select
                name="topic"
                value={formData.topic}
                label="Topic Name"
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {staticTopicData.map((topic) => (
                  <MenuItem key={topic.id} value={topic.name}>
                    {topic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={{ '& .MuiInputLabel-root': { color: theme.palette.text.primary }, '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main } }}>
              <InputLabel>Question Type</InputLabel>
              <Select
                name="questionType"
                value={formData.questionType}
                label="Question Type"
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {staticQuestionTypeData.map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Question"
              variant="outlined"
              fullWidth
              name="questionText"
              value={formData.questionText}
              onChange={handleChange}
              required
              multiline
              rows={4}
              sx={{
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !formData.course ? "Select Course" :
                !formData.module ? "Select Module" :
                !formData.topic ? "Select Topic" :
                !formData.questionType ? "Select Question Type" :
                !formData.questionText ? "Enter Question" : ""
              }
              arrow
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!formData.course || !formData.module || !formData.topic || !formData.questionType || !formData.questionText}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  UPDATE
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
            >
              CANCEL
            </Button>
          
          </Box>
        </form>

        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Question Updated ✅
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                Question details have been updated successfully.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>
    </Container>
  );
};

export default EditQuestion;
